import { Icon } from '@buggy/shared'
import { Button, Flex, Radio } from 'antd'
import { FunctionComponent, useEffect } from 'react'
import styled from 'styled-components'
import {
  useFetchUserStatusQuery,
  useUpdateUserMetadataMutation,
} from '@/api/user-api'
import { BasicModal } from '@/components/common/antd/basic-modal'
import { useOnboardingTourOptions } from '@/components/tour/onboarding-tour-modal/hooks'
import useBrandInfo from '@/hooks/use-brand-info'
import { useDispatch } from '@/hooks/use-dispatch'
import { useSelector } from '@/hooks/use-selector'
import useUserMetadata from '@/hooks/use-user-metadata'
import { useIntl } from '@/i18n/hooks/use-intl'
import {
  setSelectedTour,
  setTourModalVisible,
  TOUR_KEY_EVENT_NAME_MAPPING,
  TourKey,
} from '@/store/tour'
import { ampli } from '@/utils/ampli'
import { goToPage } from '@/utils/routes/go-to-page'
import { toVoidFunction } from '@/utils/to-void-function'

const MODAL_WIDTH = 884
const MAX_TOUR_RADIO_WIDTH = 132

type OnboardingTourModalProps = {
  open: boolean
  onClose?(): void
}

const OnboardingTourModal: FunctionComponent<OnboardingTourModalProps> = ({
  open,
  onClose,
}) => {
  const dispatch = useDispatch()
  const { formatMessage } = useIntl()

  const { brandName } = useBrandInfo()
  const { selectedTour } = useSelector((state) => state.tour)
  const { userMetadata } = useUserMetadata()
  const { data: userStatus } = useFetchUserStatusQuery()
  const [updateUserMetadata] = useUpdateUserMetadataMutation()

  const tourOptions = useOnboardingTourOptions()

  const startTourButtonText =
    tourOptions.find((option) => option.key === selectedTour)
      ?.launchButtonText ?? ''

  const handleTourModalClose = async (): Promise<void> => {
    ampli.closeOnboardingTutorial({
      onboardingItem: TOUR_KEY_EVENT_NAME_MAPPING[selectedTour],
    })

    if (userMetadata && !userMetadata.completed_onboarding) {
      await updateUserMetadata({
        ...userMetadata,
        completed_onboarding: true,
      })
    }

    onClose?.()
  }

  const handleTourStart = async (): Promise<void> => {
    const tour = tourOptions.find((option) => option.key === selectedTour)
    if (!tour) {
      return
    }

    await goToPage(tour.initUrl)
    dispatch(setTourModalVisible(true))

    onClose?.()
  }

  const handleTourChange = (tourKey: TourKey): void => {
    dispatch(setSelectedTour(tourKey))
  }

  /** NOTE: initialize selected tour value */
  useEffect(() => {
    if (open) {
      const defaultSelectedTour: TourKey =
        userStatus?.currentWorkspace.usagePurpose ===
        'predictCompetitorStrategy'
          ? 'predict-competitor'
          : 'search-kol'
      dispatch(setSelectedTour(defaultSelectedTour))
    }
  }, [dispatch, open, userStatus?.currentWorkspace.usagePurpose])

  return (
    <BasicModal
      destroyOnClose
      footer={null}
      open={open}
      width={MODAL_WIDTH}
      onCancel={toVoidFunction(handleTourModalClose)}
    >
      <ModalContent>
        <ModalTitle>Welcome to {brandName} 👋</ModalTitle>
        <ModalSubTitle>{formatMessage({ id: 'tour:sub_title' })}</ModalSubTitle>
        <TourGroupTitle>
          {formatMessage({ id: 'tour:options_title' })}
        </TourGroupTitle>
        <StyledRadioGroup value={selectedTour}>
          {tourOptions
            .filter((option) => option.permission)
            .map((tour) => (
              <RadioWrapper
                isActive={selectedTour === tour.key}
                key={tour.key}
                onClick={(): void => handleTourChange(tour.key)}
              >
                <Radio
                  value={tour.key}
                  onClick={(): void => handleTourChange(tour.key)}
                >
                  <Flex align='center' gap={12}>
                    <Icon fontSize={48} name={tour.iconName} />
                    <Flex
                      vertical
                      gap={2}
                      style={{ maxWidth: MAX_TOUR_RADIO_WIDTH }}
                    >
                      <TourCaption>{tour.tourCaption}</TourCaption>
                      <TourTitle>{tour.tourTitle}</TourTitle>
                    </Flex>
                  </Flex>
                </Radio>
              </RadioWrapper>
            ))}
        </StyledRadioGroup>
        <Flex justify='space-between'>
          <Link onClick={toVoidFunction(handleTourModalClose)}>
            {formatMessage({ id: 'tour:skip' })}
          </Link>
          <Button type='primary' onClick={toVoidFunction(handleTourStart)}>
            {startTourButtonText}
          </Button>
        </Flex>
      </ModalContent>
    </BasicModal>
  )
}

export default OnboardingTourModal

const ModalTitle = styled.div`
  font-size: 36px;
  font-weight: 700;
  line-height: 140%;
  color: ${({ theme }): string => theme.colors.text.primary};
`

const ModalSubTitle = styled.div`
  font-size: 16px;
  line-height: 140%;
  color: ${({ theme }): string => theme.colors.text.primary};
`

const TourGroupTitle = styled.div`
  color: ${({ theme }): string => theme.colors.text.primary};
  font-size: 16px;
  font-weight: 700;
  line-height: 140%;
`

const ModalContent = styled.div`
  ${ModalTitle} {
    margin: 24px 0 16px;
  }

  ${ModalSubTitle} {
    margin-bottom: 40px;
  }

  ${TourGroupTitle} {
    margin-bottom: 16px;
  }
`

const TourTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  line-height: 140%;
  color: ${({ theme }): string => theme.colors.text.primary};
`

const TourCaption = styled.div`
  font-size: 13px;
  line-height: 140%;
  color: ${({ theme }): string => theme.colors.text.tooltip};
`

const StyledRadioGroup = styled(Radio.Group)`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 40px;

  .ant-radio-wrapper {
    margin: 0;
  }

  .ant-radio-wrapper span.ant-radio + * {
    padding: 0 0 0 12px;
  }
`

const RadioWrapper = styled.div<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 16px 20px;
  border-radius: 4px;
  border: 1px solid
    ${({ theme, isActive }): string =>
      isActive ? theme.colors.brand.primary : theme.colors.border.border};

  :hover {
    border-color: ${({ theme }): string => theme.colors.brand.primaryHover};

    .ant-radio-wrapper .ant-radio-inner {
      border-color: ${({ theme }): string => theme.colors.brand.primaryHover};
    }
  }
`

const Link = styled.a`
  text-decoration: underline;
`
